// @eidollon/contracts/intel
//
// Live Intelligence Layer wire types — every signal flowing into the Intel
// page (sanctions, conflict, supply, obligations, policy, aviation, seismic,
// weather, anomalies) round-trips through these schemas.
//
// Pairs with `intelFilterSchema` in `./filters.ts` (Iron Rule 11) and the
// precomputed `intel_relevance` artifact (Iron Rule 12 — zero LLM in the
// user request path).

import { z } from 'zod';

/* ──────────────────────────────────────────────────────────────────────────
 *  Enums
 * ────────────────────────────────────────────────────────────────────────── */

/** UI severity bucket. Mapped from the numeric `severity` (0..100) by the
 *  signal-fusion worker. Display layer keys color/icon off this. */
export const Severity = z.enum(['HOT', 'HIGH', 'MED', 'LOW', 'INFO']);
export type Severity = z.infer<typeof Severity>;

/** Severity-bucket chip (rail filter) values. Four-band variant of {@link Severity}
 *  used by the `severity_bucket` facet on `intelFilterSchema`. The numeric
 *  boundaries are the single source of truth for both server-side compilation
 *  (`filter-compiler.ts`, `bucket` op) and client-side filtering
 *  (`applyFilter` in `useIntelFeed.ts`). Boundaries mirror the heat ramp in
 *  `colors.ts#severityRamp` so the chip label matches the marker color. */
export const SeverityBucket = z.enum(['HOT', 'HIGH', 'MED', 'LOW']);
export type SeverityBucket = z.infer<typeof SeverityBucket>;

/** Numeric boundaries for the `severity_bucket` chip filter. Exclusive max,
 *  inclusive min — `[min, max)`. Aligned to the W1-A heat ramp:
 *    HOT  ≥ 90
 *    HIGH 70..89
 *    MED  40..69
 *    LOW  < 40
 *  Reviewers: keep these in lock-step with `severityRamp()` in
 *  `@eidollon/intel-feed-client/colors.ts` and the `bucket` SQL builder in
 *  `services/api/src/lib/filter-compiler.ts`. */
export const SEVERITY_BUCKETS = {
  HOT: { min: 90, max: 101 },
  HIGH: { min: 70, max: 90 },
  MED: { min: 40, max: 70 },
  LOW: { min: 0, max: 40 },
} as const satisfies Record<SeverityBucket, { min: number; max: number }>;

/** Map a numeric severity (0..100) to its 4-bucket chip label. */
export function severityToBucket(severity: number): SeverityBucket {
  if (severity >= SEVERITY_BUCKETS.HOT.min) return 'HOT';
  if (severity >= SEVERITY_BUCKETS.HIGH.min) return 'HIGH';
  if (severity >= SEVERITY_BUCKETS.MED.min) return 'MED';
  return 'LOW';
}

/** The three Intel page views. URL search-param value lives at `?view=`. */
export const IntelView = z.enum(['map', 'timeline', 'analytics']);
export type IntelView = z.infer<typeof IntelView>;

/** 3-letter signal taxonomy. Matches `signal_type` filter values. */
export const SignalType = z.enum([
  'SAN', // sanctions
  'CFL', // conflict
  'SUP', // supply chain disruption
  'OBL', // contract obligations / awards
  'POL', // policy / regulatory
  'AVI', // aviation
  'SEI', // seismic
  'WEA', // weather
  'FIR', // wildfire / active fire (NASA EONET)
  'ANO', // anomaly (cross-domain detection)
  'NEW', // news / media coverage (GDELT DOC)
]);
export type SignalType = z.infer<typeof SignalType>;

/* ──────────────────────────────────────────────────────────────────────────
 *  IntelSignal — the canonical row for the map, timeline, analytics views
 * ────────────────────────────────────────────────────────────────────────── */

const Geom = z
  .object({
    lng: z.number().min(-180).max(180),
    lat: z.number().min(-90).max(90),
  })
  .strict();

const Relevance = z
  .object({
    /** 0..1 — interest-vector cosine similarity rebased to [0,1]. */
    score: z.number().min(0).max(1),
    /** Short human-readable explanation (precomputed; no LLM in hot path). */
    reason: z.string().max(280),
    bucket: Severity,
  })
  .strict();

/* ──────────────────────────────────────────────────────────────────────────
 *  AlertEnvelope — wire shape on `alerts.fanout.<tenant_id>`
 *
 *  Published by `services/intel-aoi-matcher` whenever an `IntelSignal`
 *  geometrically intersects an active AOI in `saved_screen_aois`. Consumed
 *  by `services/realtime` and re-emitted to the SSE/WS client as
 *  `event: alert`. Kept minimal — the recipient already has the full
 *  IntelSignal in its SignalsRequest cache, so the envelope only carries
 *  the pointer + the chip-rendering bits (severity colour, optional centroid,
 *  optional AOI label). Iron Rule #11: this is the single source of truth;
 *  both publisher and consumer import from here.
 * ────────────────────────────────────────────────────────────────────────── */

export const AlertEnvelope = z
  .object({
    /** Discriminator for the alerts channel. Future kinds: 'saved_view_match'. */
    kind: z.literal('aoi_match'),
    saved_screen_id: z.string().min(1).max(64),
    signal_id: z.string().min(1).max(64),
    /** 0..100 — chip colour comes from this. No LLM (Iron Rule #12). */
    severity: z.number().int().min(0).max(100),
    occurred_at: z.string().min(8),
    /** Centroid for the alert chip's mini-map indicator. Optional — polygon
     *  matches that came from `payload.geom_polygon` may not have a point. */
    geo: Geom.optional(),
    /** AOI label, if the user named the polygon. */
    label: z.string().max(120).optional(),
  })
  .strict();
export type AlertEnvelope = z.infer<typeof AlertEnvelope>;

export const IntelSignal = z
  .object({
    id: z.string().min(1).max(64),
    source: z.string().min(1).max(32), // 'OFAC' | 'EU' | 'ACLED' | 'GDELT' | 'AIS' | 'USGS' | 'NOAA' | 'internal' | …
    signal_type: SignalType,
    /** Free-form classification within the signal type (e.g. SDN add, airstrike, port delay). */
    classification: z.string().min(1).max(64),
    occurred_at: z.string().min(8), // ISO timestamp of the real-world event
    ingested_at: z.string().min(8), // ISO timestamp of when our pipeline saw it
    geom: Geom.nullable(),
    severity: z.number().int().min(0).max(100),
    confidence: z.number().min(0).max(1),
    summary: z.string().min(1).max(280),
    /** Source-specific payload — kept opaque on the wire so connectors can
     *  evolve their schemas without a contract breakage. The classification
     *  field carries the discriminator the UI keys off. */
    payload: z.record(z.string(), z.unknown()),
    linked_opportunity_ids: z.array(z.string()).max(64),
    /** Per-tenant relevance attached by the nightly fusion worker; null until
     *  the worker has processed the signal for the requesting tenant. */
    relevance: Relevance.nullable(),
    /** Credibility tier of the upstream source (1=wire/official → 4=aggregator),
     *  looked up at serialization time from `shared/source-tiers.json` via
     *  `@eidollon/contracts/source-tiers`. `null` if the row's `source` is not
     *  registered yet (logged server-side; chip falls back to "Unknown").
     *  Optional on the wire so legacy fanout payloads (services/realtime over
     *  NATS) without this field still parse — the API serializer always
     *  populates it. */
    source_tier: z
      .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])
      .nullable()
      .optional(),
    /** Registry default confidence for the source (per-row `confidence` above
     *  is what the connector actually stamped, which may differ if the source
     *  shipped a per-row value). The frontend uses this to compare a row's
     *  actual confidence against the source's baseline. `null` when
     *  unregistered. Optional for the same reason as `source_tier`. */
    source_confidence: z.number().min(0).max(1).nullable().optional(),
  })
  .strict();
export type IntelSignal = z.infer<typeof IntelSignal>;
